import { ChatClientState, StatefulChatClient } from "@azure/communication-react";
import React, { useEffect } from "react";

/**
 * Distinct list of all chat thread IDs the user is assigned to.
 * NOTE: the list is only refreshed when the value has actually changed.
 *
 * The `onStateChange` listener fires on every ACS mutation (each message, typing
 * indicator, read receipt, ...). To avoid re-subscribing on every list change and
 * to keep the per-event diff O(n), the current IDs are held in a ref and compared
 * with a `Set`. The effect therefore depends only on `chatClient`.
 */
export const useChatThreadIds = (chatClient: StatefulChatClient) => {
    const [threadIds, setThreadIds] = React.useState<string[]>([]);
    const threadIdsRef = React.useRef<string[]>(threadIds);

    useEffect(() => {
        const handleStateChange = (state: ChatClientState) => {
            const next = Object.keys(state.threads);
            const current = threadIdsRef.current;
            const currentSet = new Set(current);
            const changed = next.length !== current.length || next.some(id => !currentSet.has(id));
            if (changed) {
                threadIdsRef.current = next;
                setThreadIds(next);
            }
        };

        chatClient.onStateChange(handleStateChange);

        return () => {
            chatClient.offStateChange(handleStateChange);
        };
    }, [chatClient]);

    return threadIds;
};
